import { useCallback, useEffect, useState } from 'react';
import { Button, Col, Empty, Row, Switch, notification } from 'antd';
import { PageWrapper } from 'components/PageWrapper';
import { PageHeader } from 'components/PageHeader';
import { Heading1 } from 'components/Heading1';
import { useBreadcrumb } from 'modules/core/context/AppContext';
import { BreadcrumbContainer } from 'components/Breadcrumb';
import { UsersIcon } from 'modules/users/constants/routes';
import { useCurrentProfile } from 'modules/core/context/ProfileContext';
import { AccessListContent, AccessPermission, AccessType } from 'types/Access';
import { Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { UsersService } from 'modules/users/services';
import { AccessService } from 'modules/users/services/access';
import { SplashScreen } from 'components/SplashScreen';
import { DescriptionsVertical } from 'components/DescriptionsVertical';
import { normalizeCpf } from 'helpers/normalizers';
import { useTheme } from 'styled-components';
import { Heading2 } from 'components/Heading2';
import { Collapse, CollapseHeader } from 'components/Collapse';
import { TextM } from 'components/Text';
import { ApprovalLimitField } from 'components/ApprovalLimitField';
import ApprovalLimitConfirmation from './ApprovalLimitConfirmation';
import { ColumnsType } from 'antd/es/table';
import { Table } from 'components/Table';
import { AccessPermissionDescription } from 'constants/access';
import { useAccess } from 'modules/users/hooks/useAccess';
import { queryClient } from 'network/query';
import {
	BreadcrumbItemType,
	BreadcrumbSeparatorType,
} from 'antd/es/breadcrumb/Breadcrumb';
import { CompanyIcon } from 'modules/company/constants/routes';
import { DescriptionsVerticalWrapper, PermissionsTableWrapper } from './styles';
import {
	LimitEdit,
	isLimitDirty,
	isLimitInvalid,
	parseBRL,
	toLimitEdit,
} from './dirty';

type AccessPermissionItem = {
	access: AccessListContent;
	enabled: boolean;
	permission: AccessPermission;
};

interface IUsersDetailsProps {
	isCompany?: boolean;
}
const UsersDetails: React.FC<IUsersDetailsProps> = ({ isCompany }) => {
	const theme = useTheme();

	const [api, contextHolder] = notification.useNotification();
	const { setBreadcrumb } = useBreadcrumb();
	const { type, permissions, financial_institution } = useCurrentProfile();
	const { id, userId } = useParams();

	const currentPermissionsList =
		type !== AccessType.FINANCIAL_INSTITUTION
			? permissions
			: Object.values(AccessPermission);

	const { data, isLoading } = useQuery({
		queryKey: ['usersDetails', userId],
		queryFn: () => UsersService.getUser(userId!),
		refetchOnWindowFocus: false,
		enabled: typeof userId === 'string',
	});

	const { data: accessList, isLoading: isAccessListLoading } = useQuery({
		queryKey: ['usersAccessDetails', userId],
		refetchOnWindowFocus: false,
		queryFn: () =>
			AccessService.getAccessList({
				taxpayer_id: data!.taxpayer_id,
			}),
		enabled: typeof data === 'object',
	});

	// estado local do limite por acesso (só o limite é editável em lote via "Salvar
	// alterações"; permissões continuam sendo salvas na hora do toggle) + acesso salvando
	const [limitById, setLimitById] = useState<Record<string, LimitEdit>>({});
	const [savingId, setSavingId] = useState<string | null>(null);
	const [limitToConfirm, setLimitToConfirm] =
		useState<AccessListContent | null>(null);

	const {
		updatePermissions,
		updateApprovalLimit,
		isLoading: isUpdating,
	} = useAccess({
		onError: error => {
			setSavingId(null);
			setLimitToConfirm(null);
			api.error({
				message: 'Ocorreu um problema ao atualizar as permissões',
				description: error.data.message,
			});
		},
		onSuccess: () => {
			setSavingId(null);
			setLimitToConfirm(null);
			api.success({
				message: 'Permissões atualizadas com sucesso',
			});
			queryClient.refetchQueries({
				queryKey: ['usersAccessDetails', userId],
			});
		},
	});

	// inicializa/re-sincroniza o limite local a partir do salvo sempre que a lista muda
	// (carga inicial e refetch pós-save). Espelha o servidor.
	useEffect(() => {
		if (!accessList) return;
		const next: Record<string, LimitEdit> = {};
		accessList.content.forEach(access => {
			next[access.id] = toLimitEdit(access);
		});
		setLimitById(next);
	}, [accessList]);

	const setLimitActive = useCallback((accessId: string, active: boolean) => {
		setLimitById(prev => {
			const current = prev[accessId];
			if (!current) return prev;
			return { ...prev, [accessId]: { ...current, active } };
		});
	}, []);

	const setLimitValue = useCallback(
		(accessId: string, value: string | number) => {
			setLimitById(prev => {
				const current = prev[accessId];
				if (!current) return prev;
				return { ...prev, [accessId]: { ...current, value } };
			});
		},
		[],
	);

	// limite: PUT sob demanda pelo botão "Salvar alterações", na rota dedicada (MFA).
	// Envia só os campos de limite; permissões não vão (a rota rejeita).
	// Exige confirmação por OTP antes de enviar ao backend.
	const onSaveLimit = useCallback(
		(access: AccessListContent, mfaToken: string) => {
			const edit = limitById[access.id];
			if (!edit || !data) return;
			setSavingId(access.id);
			updateApprovalLimit({
				userId: data.id,
				accessDto: {
					type: access.type,
					personId: access.person?.id,
					postingApprovalLimit: parseBRL(edit.value),
					postingApprovalLimitActive: edit.active,
				},
				mfaToken,
			});
		},
		[limitById, data, updateApprovalLimit],
	);

	useEffect(() => {
		const items: Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[] =
			[];

		if (isCompany && accessList?.content && accessList.content.length > 0) {
			items.push(
				{
					href: '/company',
					title: (
						<BreadcrumbContainer>
							<CompanyIcon />
							<span>Clientes</span>
						</BreadcrumbContainer>
					),
				},
				{
					href: `/company/${id}/access`,
					title: (
						<BreadcrumbContainer>
							<span>{accessList.content[0].person?.name}</span>
						</BreadcrumbContainer>
					),
				},
			);
		} else {
			items.push({
				href: '/users',
				title: (
					<BreadcrumbContainer>
						<UsersIcon />
						<span>Acessos</span>
					</BreadcrumbContainer>
				),
			});
		}

		items.push({
			title: (
				<BreadcrumbContainer>
					{typeof data?.full_name === 'string' ? data.full_name : ''}
				</BreadcrumbContainer>
			),
		});

		setBreadcrumb(items);
	}, [setBreadcrumb, data, accessList, isCompany, id]);

	// permissões: PUT imediato a cada toggle, na rota dedicada. Envia só permissões;
	// o backend preserva o limite (não enviamos limite aqui).
	const onChangePermission = useCallback(
		(item: AccessPermissionItem) => () => {
			updatePermissions({
				userId: data!.id,
				accessDto: {
					permissions: item.enabled
						? item.access.permissions.filter(
								permission => permission !== item.permission,
							)
						: [...item.access.permissions, item.permission],
					type: item.access.type,
					personId: item.access.person?.id,
				},
			});
		},
		[data, updatePermissions],
	);

	const columns: ColumnsType<AccessPermissionItem> = [
		{
			title: 'Empresa',
			width: '35%',
			render: (item: AccessPermissionItem) => (
				<TextM
					style={{ color: theme.textGray, padding: '0px 16px' }}
					weight="normal"
				>
					{AccessPermissionDescription[item.permission]}
				</TextM>
			),
		},
		{
			title: 'Tipo da parte',
			width: '30%',
			align: 'end',
			render: (item: AccessPermissionItem) => {
				return (
					<div style={{ padding: '0px 16px' }}>
						<Switch
							checked={item.enabled}
							size="default"
							disabled={isUpdating}
							onChange={onChangePermission(item)}
						/>
					</div>
				);
			},
		},
	];

	if (
		type !== AccessType.FINANCIAL_INSTITUTION &&
		!permissions.includes(AccessPermission.MANAGE_USER)
	) {
		return <Navigate to="/" />;
	}

	if (isLoading || isAccessListLoading) {
		return <SplashScreen />;
	}

	if (!data) {
		return <Navigate to="/users" />;
	}

	const userAccessList = accessList?.content.filter(
		({ type }) => type !== AccessType.FINANCIAL_INSTITUTION,
	);

	return (
		<>
			{contextHolder}
			<PageHeader>
				<Heading1>{data.full_name}</Heading1>
			</PageHeader>
			<PageWrapper>
				<Row gutter={[16, 16]}>
					<Col span={24}>
						<DescriptionsVerticalWrapper>
							<DescriptionsVertical
								title="Dados básicos"
								items={[
									{
										key: '1',
										label: 'Nome',
										children: data.full_name,
									},
									{
										key: '2',
										label: 'CPF',
										children: normalizeCpf(
											data.taxpayer_id,
										),
									},
									{
										key: '3',
										label: 'E-mail',
										children: data.email_address,
									},
								]}
							/>
						</DescriptionsVerticalWrapper>
					</Col>
				</Row>
				<Row
					style={{
						marginTop: '2rem',
						border: `1px solid ${theme.border}`,
						padding: '16px 20px',
						borderRadius: '6px',
					}}
				>
					<Col span={24}>
						<Heading2>Permissões</Heading2>
						<Row style={{ marginTop: '1rem' }}>
							<Col span={24}>
								{userAccessList && userAccessList.length > 0 ? (
									<PermissionsTableWrapper>
										<Collapse
											noPadding={true}
											items={accessList?.content
												.filter(
													({ type }) =>
														type !==
														AccessType.FINANCIAL_INSTITUTION,
												)
												.map((access, i) => {
													const saved =
														toLimitEdit(access);
													const edit =
														limitById[access.id] ??
														saved;
													const dirty = isLimitDirty(
														saved,
														edit,
													);
													const invalid =
														isLimitInvalid(edit);
													const showLimit =
														access.type ===
															AccessType.PERSON &&
														access.permissions.includes(
															AccessPermission.POSTING_REVIEW,
														);
													return {
														key: `${i + 1}`,
														label: (
															<CollapseHeader>
																<TextM>
																	{access.type ===
																	AccessType.PERSON
																		? access
																				.person
																				?.name
																		: financial_institution.name}
																</TextM>
															</CollapseHeader>
														),
														children: (
															<Table
																columns={
																	columns
																}
																showHeader={
																	false
																}
																borderless={
																	true
																}
																rowKey="permission"
																dataSource={currentPermissionsList.map(
																	permission => ({
																		access,
																		enabled:
																			access.permissions.includes(
																				permission,
																			),
																		permission,
																	}),
																)}
																pagination={
																	false
																}
																expandable={{
																	showExpandColumn:
																		false,
																	expandedRowKeys:
																		showLimit
																			? [
																					AccessPermission.POSTING_REVIEW,
																				]
																			: [],
																	expandedRowRender:
																		() => (
																			<ApprovalLimitField
																				value={
																					edit.value
																				}
																				active={
																					edit.active
																				}
																				onChangeValue={value =>
																					setLimitValue(
																						access.id,
																						value,
																					)
																				}
																				onChangeActive={active =>
																					setLimitActive(
																						access.id,
																						active,
																					)
																				}
																				footer={
																					<Button
																						type="primary"
																						style={{
																							fontWeight: 700,
																						}}
																						disabled={
																							!dirty ||
																							invalid
																						}
																						loading={
																							savingId ===
																							access.id
																						}
																						onClick={() =>
																							setLimitToConfirm(
																								access,
																							)
																						}
																					>
																						Salvar
																						alterações
																					</Button>
																				}
																			/>
																		),
																}}
															/>
														),
													};
												})}
										/>
									</PermissionsTableWrapper>
								) : (
									<Empty
										description="Nenhum acesso com permissões disponível."
										image={null}
										style={{
											paddingBottom: '5rem',
											borderRadius: '10px',
										}}
									/>
								)}
							</Col>
						</Row>
					</Col>
				</Row>
			</PageWrapper>
			<ApprovalLimitConfirmation
				isOpen={!!limitToConfirm}
				isLoading={isUpdating}
				onClose={() => setLimitToConfirm(null)}
				onConfirm={mfaToken => {
					if (limitToConfirm) {
						onSaveLimit(limitToConfirm, mfaToken);
					}
				}}
			/>
		</>
	);
};

export default UsersDetails;
